import {
  Component, OnInit, computed, inject, signal,
  ChangeDetectionStrategy,
} from '@angular/core';
import { RouterLink } from '@angular/router';
import { AuthService }          from '../../../core/services/auth.service';
import { ActiveTenantService }  from '../../../core/services/active-tenant.service';
import { UserSettingsService }  from '../../../core/services/user-settings.service';
import { MeProfileService }     from '../../../core/services/me-profile.service';
import { LayoutService }        from '../../../core/services/layout.service';
import { LucideAngularModule, LogOut } from 'lucide-angular';
import { AnnouncementsService } from '../../../core/services/announcements.service';
import { WhatsNewPanelComponent } from '../../../features/whats-new/whats-new-panel.component';
import { TenantSwitcherComponent } from '../../components/tenant-switcher/tenant-switcher.component';
import { AvatarComponent } from '../../components/avatar/avatar.component';

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  selector: 'app-topbar',
  standalone: true,
  imports: [
    RouterLink,
    LucideAngularModule,
    WhatsNewPanelComponent,
    TenantSwitcherComponent,
    AvatarComponent,
  ],
  templateUrl: './topbar.component.html',
})
export class TopbarComponent implements OnInit {
  readonly authService              = inject(AuthService);
  protected readonly layout         = inject(LayoutService);
  private readonly userSettings     = inject(UserSettingsService);
  protected readonly meProfile      = inject(MeProfileService);
  private readonly activeTenantService = inject(ActiveTenantService);
  protected readonly announcementsService = inject(AnnouncementsService);
  readonly LogOutIcon = LogOut;

  ngOnInit(): void {
    // The topbar is always mounted, so it loads the current user's profile
    // projection (avatar + names) once for the whole app.
    this.meProfile.load();
  }

  protected readonly whatsNewOpen = signal(false);

  protected readonly roleBadgeLabel = computed(() => {
    const role = this.activeTenantService.activeTenantRole();
    const labels: Record<string, string> = {
      owner: 'Tenant Owner',
      member: 'Member',
    };
    return role ? (labels[role] ?? role) : null;
  });

  readonly displayLabel = computed(() => {
    const custom = this.userSettings.displayName().trim();
    if (custom) return custom;
    const user = this.authService.currentUser();
    return user?.name || user?.email || '';
  });

  readonly initials = computed(() => {
    const label = this.displayLabel();
    return label ? label.charAt(0).toUpperCase() : '?';
  });

  logout(): void { this.authService.logout(); }

  closeWhatsNew(): void { this.whatsNewOpen.set(false); }

  onBackdropClick(e: Event): void { if (e.target === e.currentTarget) this.closeWhatsNew(); }
}
